/**
 * Experience Mode Context
 * Manages demo/real mode state and syncs the x-sfi-experience-mode header
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { setExtraHeaders } from '../services/apiService'

export type ExperienceMode = 'real' | 'demo'

interface ExperienceModeContextValue {
  experienceMode: ExperienceMode
  enterDemoMode: () => void
  exitDemoMode: () => void
}

const ExperienceModeContext = createContext<ExperienceModeContextValue | null>(null)

export function ExperienceModeProvider({ children }: { children: ReactNode }) {
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>('real')

  const enterDemoMode = useCallback(() => setExperienceMode('demo'), [])
  const exitDemoMode = useCallback(() => setExperienceMode('real'), [])

  useEffect(() => {
    if (experienceMode === 'demo') {
      setExtraHeaders({ 'x-sfi-experience-mode': 'demo' })
    } else {
      setExtraHeaders({})
    }
  }, [experienceMode])

  return (
    <ExperienceModeContext.Provider value={{ experienceMode, enterDemoMode, exitDemoMode }}>
      {children}
    </ExperienceModeContext.Provider>
  )
}

export function useExperienceMode(): ExperienceModeContextValue {
  const context = useContext(ExperienceModeContext)
  if (!context) {
    throw new Error('useExperienceMode must be used within an ExperienceModeProvider')
  }
  return context
}
